import React, { useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { isDate, isValid } from 'date-fns';
import dayjs from 'dayjs';
import { FormError } from './hook-form-error';
import { LoadingIndicator } from '../loading-indicator.component';
//import { useSnackbar } from '../../hooks/use-snackbar.hook';
import Tooltip from '@mui/material/Tooltip';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import { Dialog, DialogTitle, DialogContent,  DialogActions, Button,} from '@mui/material';

export enum DateFormat {
    SIMPLE_DATE = 'MM/DD/YYYY',
}

interface DateLimits {
    maxDateEnabled?: boolean;
    maxDateValue?: string;
    minDateEnabled?: boolean;
    minDateValue?: string;
}
interface DateSelectFieldProps {
    label: string;
    name: string;
    isParentLoading?: boolean;
    onChange?: any;
    onBlur?: any;
    disabled?: boolean;
    format?: DateFormat;
    required?: boolean;
    dateLimits?: DateLimits;
    restrictDateSelection?: boolean;
    holidayDateList?: any[];
    enableDateConfirmation?: boolean;
}

const convertToDate = (value: any) => {
    if (value === null || value === undefined || value === 'Invalid Date') {
        return null;
    }
    if (isDate(value)) {
        return value;
    }
    if (isValid(new Date(value))) {
        return dayjs(value);
    }
    return null;
};

export const formatSiTrackDate = (value: any, format?: DateFormat) => {
    if (value == null) return '';
    const date = dayjs(value);

    return format ? date.format(format) : date.toString();
};

export const FormDateField = ({
    name,
    label,
    required,
    format = DateFormat.SIMPLE_DATE,
    disabled,
    onChange,
    onBlur,
    restrictDateSelection,
    holidayDateList,
    enableDateConfirmation = false,
    isParentLoading,
    dateLimits = {},
}: DateSelectFieldProps): any => {
    const { control, setValue, getValues } = useFormContext();
    //const { showSnackbar } = useSnackbar();

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [pendingDate, setPendingDate] = useState<any>(null);
    const [dialogMessage, setDialogMessage] = useState('');

    const {
        maxDateEnabled = false,
        maxDateValue = dateLimits?.maxDateValue ?? '',
        minDateEnabled = false,
        minDateValue = dateLimits?.minDateValue ?? '',
    } = dateLimits;

    useWatch({
        name: [maxDateValue, minDateValue] || [],
    });

    //const handleChange = (date: any) => {
    //    if (!date) {
    //        setValue(name, "");
    //        return;
    //    }

    //    const selectedDate = dayjs(date, format, true);

    //    const year = selectedDate.year();

    //    const formattedDate =
    //        selectedDate.isValid() && year >= 1900 && year <= 2100
    //            ? selectedDate.format(format)
    //            : "";

    //    const day =
    //        selectedDate.isValid() && year >= 1900 && year <= 2100
    //            ? selectedDate.day()
    //            : -1;

    //    if (restrictDateSelection && (day === 0 || day === 6)) {
    //        setValue(name, "");
    //        showSnackbar(
    //            'Weekend dates are not allowed. Please choose a weekday.',
    //            'warning'
    //        );
    //        return;
    //    }

    //    if (holidayDateList?.includes(formattedDate)) {
    //        setValue(name, "");
    //        showSnackbar(
    //            'Selected date is a holiday. Please choose another date.',
    //            'warning'
    //        );
    //        return;
    //    }

    //    if (onChange) {
    //        onChange(date);
    //    }
    //};

    const handleChange = (date: any) => {
        if (!date) {
            setValue(name, '');
            return;
        }

        const selectedDate = dayjs(date, format, true);

        const formattedDate = selectedDate.format(format);
       
        const day = selectedDate.day();

        const isWeekend = day === 0 || day === 6;

        const holiday = holidayDateList?.find(
            (h: any) => h.date === formattedDate
        );

        const isHoliday = Boolean(holiday);

        if (
            enableDateConfirmation &&
            (isWeekend || isHoliday)
        ) {
            setPendingDate(formattedDate);

            setDialogMessage(
                isHoliday
                    ? `Selected date is a holiday (${holiday?.name}). Do you want to continue?`
                    : 'Selected date falls on a weekend. Do you want to continue?'
            );

            setConfirmOpen(true);
            return;
        }

        setValue(name, formattedDate);

        if (onChange) {
            onChange(formattedDate);
        }
    };

    const handleConfirmOk = () => {
        setValue(name, pendingDate);

        if (onChange) {
            onChange(pendingDate);
        }

        setPendingDate(null);
        setConfirmOpen(false);
    };

    const handleConfirmCancel = () => {
        setValue(name, '');
        setPendingDate(null);
        setConfirmOpen(false);
    };

    const handleBlur = (date: any) => { onBlur(date); }

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Controller
                control={control}
                name={name}
                render={({ field: { onChange, value } }) => {
                    return (
                        <>
                            <LoadingIndicator isLoading={isParentLoading} />
                            <DatePicker
                                onChange={(date: any) => {
                                    onChange(formatSiTrackDate(convertToDate(date), format))
                                    handleChange(formatSiTrackDate(convertToDate(date), format));
                                }}
                                value={convertToDate(value)}
                                shouldDisableDate={(date) => {
                                    const formattedDate = dayjs(date).format(format);

                                    const isWeekend =
                                        (dayjs(date).day() === 0 || dayjs(date).day() === 6);

                                    const isHoliday =
                                        holidayDateList?.some(
                                            (h: any) => h.date === formattedDate
                                        ) ?? false;
                                    return !!restrictDateSelection && (isWeekend ||  isHoliday);
                                }}
                                slotProps={{
                                    textField: {
                                        size: 'small',
                                        label: `${label}`,
                                        onBlur: handleBlur,
                                        required,
                                    },
                                    //    day: (ownerState: any) => {
                                    //        const date = ownerState.day;
                                    //        const formattedDate = dayjs(date).format(format);

                                    //        const isWeekend =
                                    //            !!restrictDateSelection &&
                                    //            (dayjs(date).day() === 0 || dayjs(date).day() === 6);

                                    //        const isHoliday =
                                    //            holidayDateList?.includes(formattedDate) ?? false;

                                    //        return {
                                    //            sx: {
                                    //                // ? Remove TODAY circle
                                    //                '&.MuiPickersDay-today': {
                                    //                    border: 'none',
                                    //                },

                                    //                // ? Holiday styling ONLY
                                    //                ...(isHoliday && {
                                    //                    backgroundColor: '#FFA2A2 !important',
                                    //                    color: 'black !important',
                                    //                    borderRadius: '50%',
                                    //                }),
                                    //            },
                                    //        };
                                    //    }
                                }}
                                slots={{
                                    day: (props: any) => {
                                        const date = props.day;
                                        const formattedDate = dayjs(date).format(format);

                                        const holiday = holidayDateList?.find((h: any) => {
                                            const d = h.date;
                                            if (!d) return false;

                                            return new Date(d).toDateString() === new Date(formattedDate).toDateString();
                                        });

                                        const isHoliday = Boolean(holiday);
                                        const holidayName = holiday?.name ?? '';

                                        return (
                                            <Tooltip
                                                title={holidayName}
                                                arrow
                                                placement="top"
                                                slotProps={{
                                                    popper: {
                                                        modifiers: [
                                                            {
                                                                name: 'offset',
                                                                options: {
                                                                    offset: [0, -8], // keep it close
                                                                },
                                                            },
                                                        ],
                                                    },
                                                    tooltip: {
                                                        className: 'holiday-tooltip',
                                                        sx: {
                                                            padding: '3px 10px', //  
                                                            fontSize: '14px',

                                                        },
                                                    },
                                                    arrow: {
                                                        className: 'holiday-tooltip-arrow',
                                                    },
                                                }}
                                            >
                                                <span style={{ display: 'inline-flex' }}>
                                                    <PickersDay
                                                        {...props}
                                                        sx={{
                                                            '&.MuiPickersDay-today': {
                                                                border: 'none',
                                                            },
                                                            ...(isHoliday && {
                                                                backgroundColor: '#FFA2A2 !important',
                                                                color: 'black !important',
                                                                borderRadius: '50%',
                                                            }),
                                                        }}
                                                    />
                                                </span>
                                            </Tooltip>
                                        );
                                    },
                                }}
                                sx={{ width: '100%' }}
                                maxDate={maxDateEnabled ? dayjs(getValues(maxDateValue)) : undefined}
                                minDate={minDateEnabled ? dayjs(getValues(minDateValue)) : undefined}
                                disabled={disabled}
                            />
                            <FormError name={name} />

                            {enableDateConfirmation && (
                                <Dialog
                                    open={confirmOpen}
                                    onClose={handleConfirmCancel}
                                >
                                    <DialogTitle>Confirmation</DialogTitle>
                                    <DialogContent>
                                        {dialogMessage}
                                    </DialogContent>
                                    <DialogActions>
                                        <Button onClick={handleConfirmCancel}>
                                            Cancel
                                        </Button>
                                        <Button
                                            onClick={handleConfirmOk}
                                            variant="contained"
                                        >
                                            OK
                                        </Button>
                                    </DialogActions>
                                </Dialog>
                            )}
                        </>
                    );
                }}
            />
        </LocalizationProvider>
    );
};

FormDateField.defaultProps = {
    label: undefined,
    onChange: () => {
        return;
    },
    onBlur: () => {
        return;
    },
};

export default FormDateField;